import DateTimeService from '@app/Services/DateTimeService';
import { PersonModel } from './WebApiModels';

export type RosterCell = {
    date: Date;
    person: RosterCellPerson;
};

export type RosterCellPerson = {
    id: string;
    code: string;
};

export type FlightTarget = {
    id: string;
    airlineRcd: string;
    number: number;
    date: Date;
    legSequenceNumber: number;
};
export type FlightUrgentTarget = FlightTarget & {
    isCrewCompleteSuppresed: boolean;
};

export type FlightInformation = FlightTarget & {
    airlineRcd: string;
    originRcd: string;
    destinationRcd: string;
};

export type RotationTarget = {
    id: string;
    flights: FlightTarget[];
    isLongHaul: boolean;
};

class RosterCellFormatter {
    public formatRange(targetCells: RosterCell[]) {
        if (!targetCells.length) return '';

        const selectedPersonList = new Set<string>();
        const selectedDateList = new Set<number>();

        targetCells.forEach((cell) => {
            selectedPersonList.add(cell.person.code);
            selectedDateList.add(cell.date.getTime());
        });

        let minDate: Date | null = null;
        let maxDate: Date | null = null;
        const isOneDate: boolean = selectedDateList.size === 1;
        if (!isOneDate) {
            const dates = Array.from(selectedDateList)
                .sort()
                .map((d) => new Date(d));
            minDate = dates[0];
            maxDate = dates[dates.length - 1];
        }

        const personCodes = [...selectedPersonList];
        const dateTitle = isOneDate ? DateTimeService.toUiDate(targetCells[0].date) : `${minDate && DateTimeService.toUiDate(minDate)} - ${maxDate && DateTimeService.toUiDate(maxDate)} (multi-edit mode)`;
        const personTitle = personCodes.slice(0, 5).join(', ') + (personCodes.length > 5 ? ` and ${personCodes.length - 5} more` : '');
        return `for ${personTitle} on ${dateTitle}`;
    }
}

class TargetConstructor {
    private _parseRange = (range: string, reverse: boolean) => {
        const bytes = [];
        for (let i = 0; i < range.length; i += 2) {
            const byte = parseInt(range.substr(i, 2), 16);
            bytes.push(byte);
        }

        reverse && bytes.reverse();
        return bytes;
    };

    public fromFlightId = (flightId: string): FlightTarget => {
        //    Guid:  35918bc9-196d-40ea-9779-889d79b753f0
        //    Bytes: C9 8B 91 35 6D 19 EA 40 97 79 88 9D 79 B7 53 F0
        const guid = flightId.split('-');
        const bytes: number[] = [];

        bytes.push(...this._parseRange(guid[0], true));
        bytes.push(...this._parseRange(guid[1], true));
        bytes.push(...this._parseRange(guid[2], true));
        bytes.push(...this._parseRange(guid[3], false));
        bytes.push(...this._parseRange(guid[4], false));

        let year = bytes[0];
        if (year >= 90)
        {
            year -= 100;
        }
        const date = DateTimeService.date(2000 + year, bytes[1] - 1, bytes[2]);
        const airlineRcd = String.fromCharCode(bytes[3]) + String.fromCharCode(bytes[4]);
        const flightNumber = bytes[5] + bytes[6] * 256;
        const legSequenceNumber = bytes[13];

        return {
            id: flightId,
            date: date,
            airlineRcd: airlineRcd,
            number: flightNumber,
            legSequenceNumber: legSequenceNumber
        };
    };

    public getStringFrom = (id: string): string => {
        //    Guid:  35918bc9-196d-40ea-9779-889d79b753f0
        //    Bytes: C9 8B 91 35 6D 19 EA 40 97 79 88 9D 79 B7 53 F0
        const guid = id.split('-');
        const bytes: number[] = [];

        bytes.push(...this._parseRange(guid[0], true));
        bytes.push(...this._parseRange(guid[1], true));
        bytes.push(...this._parseRange(guid[2], true));
        bytes.push(...this._parseRange(guid[3], false));
        bytes.push(...this._parseRange(guid[4], false));

        const result = bytes.filter(x => x !== 0).map(x => String.fromCharCode(x)).join('');
        return result;
    };

    public getGuidFromString = (str: string): string => {
        const bytes = Array.from(str).map(c => c.charCodeAt(0));
     
        const pad = (arr: number[], size: number) => {
            while (arr.length < size) arr.push(0);
            return arr;
        };
     
        const segment = (arr: number[], start: number, length: number, reverse: boolean) => {
            const seg = arr.slice(start, start + length);
            return (reverse ? seg.reverse() : seg)
                .map(b => b.toString(16).padStart(2, '0'))
                .join('');
        };
     
        const b = pad(bytes, 16);
        const part1 = segment(b, 0, 4, true);
        const part2 = segment(b, 4, 2, true);
        const part3 = segment(b, 6, 2, true);
        const part4 = segment(b, 8, 2, false);
        const part5 = segment(b, 10, 6, false);
     
        return `${part1}-${part2}-${part3}-${part4}-${part5}`;
    };

    public getFlightInformationFromFlightId = (flightId: string): FlightInformation => {
        //    Guid:  35918bc9-196d-40ea-9779-889d79b753f0
        //    Bytes: C9 8B 91 35 6D 19 EA 40 97 79 88 9D 79 B7 53 F0
        const guid = flightId.split('-');
        const bytes = [];

        bytes.push(...this._parseRange(guid[0], true));
        bytes.push(...this._parseRange(guid[1], true));
        bytes.push(...this._parseRange(guid[2], true));
        bytes.push(...this._parseRange(guid[3], false));
        bytes.push(...this._parseRange(guid[4], false));

        let year = bytes[0];
        if (year >= 90)
        {
            year -= 100;
        }
        const date = DateTimeService.date(2000 + year, bytes[1] - 1, bytes[2]);
        const flightNumber = bytes[5] + bytes[6] * 256;
        const airlineRcd = String.fromCharCode(bytes[3], bytes[4]);
        const originRcd = String.fromCharCode(bytes[7], bytes[8], bytes[9]);
        const destinationRcd = String.fromCharCode(bytes[10], bytes[11], bytes[12]);
        const legSequenceNumber = bytes[13];

        return {
            id: flightId,
            date: date,
            number: flightNumber,
            destinationRcd: destinationRcd,
            originRcd: originRcd,
            airlineRcd: airlineRcd,
            legSequenceNumber: legSequenceNumber
        };
    };

    public fromPersonDate(person: PersonModel | RosterCellPerson, date: Date) {
        const target: RosterCell = {
            date: date,
            person: {
                id: person.id,
                code: person.code ? person.code : ''
            }
        };
        return target;
    }
}

export const targetConstructor = new TargetConstructor();
export const rosterCellFormatter = new RosterCellFormatter();
